import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { IExerciseResponse } from './knowledge-area-level-exercise.interface';

import { axiosApiInstance } from 'services/config';
import {
  IExerciseCategory,
  IExerciseCategoryNames,
  IExerciseRequest,
} from 'types/domain/exercise';
import {
  IAttachmentDownloadResponse,
  IAttachmentUploadUrlResponse,
} from 'utils/attachments';

class KnowledgeAreaLevelExercisesApi {
  async getAllExercisesByLevel(
    levelId: number,
    config?: AxiosRequestConfig,
  ): Promise<IExerciseResponse[]> {
    const response: AxiosResponse<IExerciseResponse[]> = await axiosApiInstance.get(
      `exercise/level/${levelId}`,
      config,
    );
    return response.data;
  }

  async createAttachmentUploadUrl(
    fileName: string,
    contentType: string,
  ): Promise<IAttachmentUploadUrlResponse> {
    const response: AxiosResponse<IAttachmentUploadUrlResponse> =
      await axiosApiInstance.post('exercise/attachment/upload-url', {
        fileName,
        contentType,
      });
    return response.data;
  }

  async getAttachmentDownloadUrl(
    attachmentId: number,
  ): Promise<IAttachmentDownloadResponse> {
    const response: AxiosResponse<IAttachmentDownloadResponse> =
      await axiosApiInstance.get(`exercise/attachment/${attachmentId}/download-url`);
    return response.data;
  }

  // Best-effort cleanup for a quarantined upload that was never linked to an
  // Exercise (removed from the form, or the form was cancelled). Safe to
  // ignore failures — an unswept upload is cleaned up server-side after 24h.
  async deletePendingAttachment(storageKey: string): Promise<void> {
    await axiosApiInstance.delete('exercise/attachment/pending', {
      params: { storageKey },
    });
  }

  async create(
    exerciseData: IExerciseRequest,
    config?: AxiosRequestConfig,
  ): Promise<IExerciseResponse> {
    const response: AxiosResponse<IExerciseResponse> = await axiosApiInstance.post(
      'exercise',
      exerciseData,
      config,
    );
    return response.data;
  }

  async update(
    exerciseId: number,
    exerciseData: IExerciseRequest,
  ): Promise<IExerciseResponse> {
    const response: AxiosResponse<IExerciseResponse> = await axiosApiInstance.put(
      `exercise/${exerciseId}`,
      exerciseData,
    );
    return response.data;
  }

  async delete(exerciseId: number, config?: AxiosRequestConfig): Promise<void> {
    await axiosApiInstance.delete(`exercise/${exerciseId}`, config);
  }

  async getExercisesCategories(): Promise<IExerciseCategoryNames> {
    const response: AxiosResponse<[IExerciseCategory, IExerciseCategory]> =
      await axiosApiInstance.get(`ex-category`);
    return response.data.map(category => category.name) as IExerciseCategoryNames;
  }
}

export { KnowledgeAreaLevelExercisesApi };
